'use client';

/**
 * Client-side search input for the help center. Debounces user input,
 * calls `GET /api/help/search`, and renders a result list inline.
 *
 * Lives under `src/app/help/_components/` so it's collocated with the
 * routes that use it (Track I owns this surface).
 */
import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';

type ApiResult = {
  slug: string;
  category: string;
  title: string;
  excerpt: string;
  updated_at: string | null;
};

type ApiResponse =
  | { ok: true; query: string; count: number; results: ApiResult[] }
  | { ok: false; error: string };

/**
 * Search bar with debounced API calls and inline result list.
 *
 * @param props.placeholder - Optional input placeholder.
 * @param props.autoFocus - When true, focus the input on mount.
 */
export function HelpSearchBar(props: {
  placeholder?: string;
  autoFocus?: boolean;
}): React.JSX.Element {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ApiResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setError(null);
      return;
    }
    const t = setTimeout(async () => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(
          `/api/help/search?q=${encodeURIComponent(trimmed)}&limit=10`,
          { signal: ctrl.signal },
        );
        const data = (await res.json()) as ApiResponse;
        if (!('ok' in data) || !data.ok) {
          setError('Search unavailable');
          setResults([]);
        } else {
          setResults(data.results);
        }
      } catch (err) {
        if ((err as { name?: string }).name === 'AbortError') return;
        setError('Search unavailable');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [query]);

  return (
    <div data-testid="help-search">
      <label
        htmlFor="help-search-input"
        style={{
          display: 'block',
          fontFamily: 'var(--font-geist-mono)',
          fontSize: '11px',
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: 'var(--dimmer)',
          margin: '0 0 8px',
        }}
      >
        {'Search help'}
      </label>
      <input
        id="help-search-input"
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={props.placeholder ?? 'How do I…'}
        autoFocus={props.autoFocus}
        autoComplete="off"
        spellCheck={false}
        style={{
          width: '100%',
          padding: '14px 16px',
          fontFamily: 'var(--font-geist-sans)',
          fontSize: '17px',
          color: 'var(--paper)',
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid var(--hair-2)',
          borderRadius: '12px',
          outline: 'none',
        }}
      />
      {loading && (
        <p
          style={{
            fontFamily: 'var(--font-geist-mono)',
            fontSize: '11px',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'var(--dimmer)',
            margin: '12px 0 0',
          }}
        >
          {'Searching…'}
        </p>
      )}
      {error && (
        <p
          style={{
            color: 'var(--amber-glow)',
            fontFamily: 'var(--font-geist-sans)',
            fontSize: '14px',
            margin: '12px 0 0',
          }}
        >
          {error}
        </p>
      )}
      {results.length > 0 && (
        <ul
          data-testid="help-search-results"
          style={{
            listStyle: 'none',
            padding: 0,
            margin: '16px 0 0',
            display: 'grid',
            gap: '8px',
          }}
        >
          {results.map((r) => (
            <li key={`${r.category}/${r.slug}`}>
              <Link
                href={`/help/${r.category}/${r.slug}`}
                data-testid="help-search-result"
                style={{
                  display: 'block',
                  padding: '12px 14px',
                  border: '1px solid var(--hair)',
                  borderRadius: '10px',
                  color: 'inherit',
                  textDecoration: 'none',
                }}
              >
                <div
                  style={{
                    fontFamily: 'var(--font-geist-mono)',
                    fontSize: '11px',
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: 'var(--dimmer)',
                  }}
                >
                  {r.category}
                </div>
                <div
                  style={{
                    fontFamily: 'var(--font-geist-sans)',
                    fontSize: '16px',
                    color: 'var(--paper)',
                    margin: '2px 0 4px',
                  }}
                >
                  {r.title}
                </div>
                <div
                  style={{
                    fontFamily: 'var(--font-geist-sans)',
                    color: 'var(--dim)',
                    fontSize: '13px',
                    lineHeight: 1.5,
                  }}
                >
                  {r.excerpt}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
      {query.trim().length >= 2 && !loading && !error && results.length === 0 && (
        <p
          style={{
            color: 'var(--dim)',
            fontFamily: 'var(--font-geist-sans)',
            fontSize: '14px',
            margin: '12px 0 0',
          }}
        >
          {'No results — try different keywords.'}
        </p>
      )}
    </div>
  );
}
